import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, WifiOff, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import { useAutosaveDraft, loadDraft, clearDraft } from "@/lib/drafts";

const TOPICS = [
  "MLILY mattress range", "Adjustable beds", "Product features and benefits",
  "Product comparison", "Sales techniques", "Mattress technology",
  "Warranty process", "Product care", "New product launch", "Promotional campaign",
];
const OUTCOMES = ["Successful", "Partial", "Follow-up Required", "Cancelled"];

const EMPTY = {
  customer_id: "", training_date: new Date().toISOString().slice(0, 10), training_start: "", training_finish: "",
  training_topic: "MLILY mattress range", product_trained: "", training_material: "",
  number_of_attendees: 0, attendee_names: [], attendee_positions: [],
  store_manager_present: false, questions_asked: "", feedback_received: "",
  additional_training_required: false, followup_date: "", training_outcome: "Successful",
  agent_notes: "", trip_id: "", attendee_signatures: [],
};

export default function TrainingReportForm() {
  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();
  const draftKey = "training_new";

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/trips").then(r => setTrips(r.data));
    const d = loadDraft(draftKey);
    if (d?.value) {
      setF({ ...EMPTY, ...d.value });
      toast.info("Restored unsaved training draft");
    }
    setLoaded(true);
  }, []);

  const { savedAt, online } = useAutosaveDraft(draftKey, f, loaded);

  useEffect(() => {
    if (online && loaded) {
      const pending = loadDraft("training_offline_pending");
      if (pending?.value?.customer_id) {
        (async () => {
          try {
            await api.post("/training-reports", pending.value);
            clearDraft("training_offline_pending");
            toast.success("Offline training report synced");
          } catch {}
        })();
      }
    }
  }, [online, loaded]);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const updList = (k) => (e) => setF({ ...f, [k]: e.target.value.split(",").map(s => s.trim()).filter(Boolean) });
  const updDate = (k) => (v) => setF({ ...f, [k]: v });
  const updSelect = (k) => (v) => setF({ ...f, [k]: v });

  const addSignature = () => setF({ ...f, attendee_signatures: [...f.attendee_signatures, ""] });
  const setSig = (i, val) => {
    const arr = [...f.attendee_signatures];
    arr[i] = val;
    setF({ ...f, attendee_signatures: arr });
  };
  const removeSig = (i) => setF({ ...f, attendee_signatures: f.attendee_signatures.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id) return toast.error("Select a customer");
    setBusy(true);
    try {
      const payload = { ...f, number_of_attendees: Number(f.number_of_attendees) || 0 };
      ["followup_date"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (payload.trip_id === "none") payload.trip_id = null;
      if (!online) {
        const { saveDraft } = await import("@/lib/drafts");
        saveDraft("training_offline_pending", payload);
        toast.info("Offline — saved locally, will sync when online");
        clearDraft(draftKey);
        nav("/training");
        return;
      }
      await api.post("/training-reports", payload);
      clearDraft(draftKey);
      toast.success("Training report submitted");
      nav("/training");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const L = ({ label, children }) => (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
  const inp = "w-full h-12 px-3 border border-slate-300 rounded-lg focus:border-[#003DA5] outline-none";

  const Sh = ({ value, onChange, options, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-12 border-slate-300 bg-white" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent className="bg-white">{options.map(o => <SelectItem key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="training-report-form">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-heading font-black tracking-tight">Training Report</h1>
        <div className="text-xs flex items-center gap-2">
          {!online && <span className="badge-warning px-2 py-1 rounded-full flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline</span>}
          {savedAt && <span className="text-slate-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Auto-saved</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Customer / Store *">
            <Sh value={f.customer_id} onChange={updSelect("customer_id")} options={customers.map(c => ({ value: c.id, label: `${c.name} — ${c.town}` }))} testid="tr-customer-select" />
          </L>
          <L label="Related Trip">
            <Sh value={f.trip_id} onChange={updSelect("trip_id")} options={[{ value: "none", label: "— None —" }, ...trips.map(t => ({ value: t.id, label: t.name }))]} />
          </L>
          <L label="Training Date *"><DatePicker value={f.training_date} onChange={updDate("training_date")} testid="tr-date" /></L>
          <L label="Follow-up Date"><DatePicker value={f.followup_date} onChange={updDate("followup_date")} /></L>
          <L label="Start Time"><input type="time" value={f.training_start} onChange={upd("training_start")} className={inp} /></L>
          <L label="Finish Time"><input type="time" value={f.training_finish} onChange={upd("training_finish")} className={inp} /></L>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold">Training Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Training Topic"><Sh value={f.training_topic} onChange={updSelect("training_topic")} options={TOPICS} /></L>
          <L label="Product Trained"><input value={f.product_trained} onChange={upd("product_trained")} className={inp} /></L>
          <L label="Training Material Used"><input value={f.training_material} onChange={upd("training_material")} className={inp} /></L>
          <L label="# Attendees"><input type="number" min="0" value={f.number_of_attendees} onChange={upd("number_of_attendees")} className={inp} data-testid="tr-attendees" /></L>
          <L label="Attendee Names (comma-separated)"><input value={f.attendee_names.join(", ")} onChange={updList("attendee_names")} className={inp} /></L>
          <L label="Attendee Positions"><input value={f.attendee_positions.join(", ")} onChange={updList("attendee_positions")} className={inp} /></L>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.store_manager_present} onChange={upd("store_manager_present")} className="w-4 h-4" /> Store manager present</label>
        <L label="Questions Asked"><textarea value={f.questions_asked} onChange={upd("questions_asked")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
        <L label="Feedback Received"><textarea value={f.feedback_received} onChange={upd("feedback_received")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.additional_training_required} onChange={upd("additional_training_required")} className="w-4 h-4" /> Additional training required</label>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Training Outcome"><Sh value={f.training_outcome} onChange={updSelect("training_outcome")} options={OUTCOMES} /></L>
        </div>
        <L label="Agent Notes"><textarea value={f.agent_notes} onChange={upd("agent_notes")} rows={3} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-heading font-semibold">Attendee Signatures</h3>
          <button type="button" onClick={addSignature} className="h-9 px-3 border border-slate-300 rounded-lg text-sm font-semibold flex items-center gap-1 hover:border-[#003DA5]" data-testid="add-signature-btn">
            <Plus className="w-4 h-4" /> Add Signer
          </button>
        </div>
        {f.attendee_signatures.length === 0 && <p className="text-sm text-slate-500">Optional — capture signatures for compliance records.</p>}
        <div className="space-y-4">
          {f.attendee_signatures.map((sig, i) => (
            <div key={i} className="relative">
              <SignaturePad
                value={sig}
                onChange={(v) => setSig(i, v)}
                label={`Attendee ${i + 1}${f.attendee_names[i] ? ` — ${f.attendee_names[i]}` : ""}`}
                testid={`signature-pad-${i}`}
              />
              <button type="button" onClick={() => removeSig(i)} className="absolute top-0 right-14 text-red-600 hover:text-red-800 text-xs flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" disabled={busy} className="mlily-btn w-full h-12 rounded-lg font-semibold flex items-center gap-2 justify-center" data-testid="submit-training-btn">
        <Save className="w-4 h-4" />{busy ? "Saving…" : "Submit Training Report"}
      </button>
    </form>
  );
}
